import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, onSnapshot, QuerySnapshot, DocumentData, orderBy, limit } from 'firebase/firestore';
import { getFailoverAwareDatabase, getFailoverAwareAuth } from '../config/firebaseWithFailover';
import { onAuthStateChanged } from 'firebase/auth';
import {
  getNotificationState,
  updatePageVisit,
  updateLoadVisit,
  markNotificationsAsRead,
  markLoadNotificationsAsRead,
  cleanupReadNotifications,
  isNewerThanPageVisit,
} from '../services/notificationTrackingService';
import Logger from '../services/logger';
import { NOTIFICATION_TYPES } from '../constants/notificationTypes';

interface Notification {
  id: string;
  type: string;
  read: boolean;
  createdAt?: any; // Firestore Timestamp or Date
  updatedAt?: any;
  message?: string;
  senderId?: string;
  senderName?: string;
  recipientId?: string;
  poNumber?: string;
  status?: string;
  requiresAction?: boolean;
  loadDetails?: any;
  brokerId?: string;
  carrierId?: string;
  loadId?: string; // Added for load-specific tracking
  [key: string]: any;
}

interface NotificationCounts {
  purchaseOrders: number;
  shippingSchedule: number;
  driverUpdates: number;
  carrierPartners: number;
  payments: number;
  bellOnly: number;
  total: number;
}

// Broker-specific notification allocation
const BROKER_NOTIFICATION_ALLOCATION = {
  SIDEBAR_DOTS: {
    purchaseOrders: [
      // NOTE: carrier_decline, make_offer, carrier_counter_offer are handled by dashboard "Action Required" cards
      // They should NOT show as sidebar dots or bell notifications
      'carrier_accept',            // Carrier accepted load
      'carrier_accept_broker',     // Broker carrier accepted load
      'load_completed',            // Load completed by carrier
      'load_status_update',        // Load status changes
      'load_modified',             // Load modifications
      NOTIFICATION_TYPES.LOAD_CANCELLED,            // Load cancellations
      NOTIFICATION_TYPES.CANCELLATION_APPROVED,    // Carrier approved cancellation (Phase 3)
      NOTIFICATION_TYPES.CANCELLATION_REJECTED,    // Carrier rejected cancellation (Phase 3)
      NOTIFICATION_TYPES.TONU_INVOICE_CREATED,    // TONU invoice created (Phase 3)
      'unified_partner_request'    // Marketplace carrier acceptance - triggers Carrier Review button
    ],
    shippingSchedule: [
      'carrier_assigned',          // Carrier assigned to load
      'load_tracking_update',      // Load tracking updates
      'load_request'               // New load requests from carriers
    ],
    driverUpdates: [
      'load_tracking_update',      // Driver status updates
      'load_status_update',       // Load progress updates
      NOTIFICATION_TYPES.LOAD_STARTED,   // Carrier started load (Phase 4)
      NOTIFICATION_TYPES.LOAD_OFF_ROUTE  // Carrier off route (Phase 4, optional)
    ],
    carrierPartners: [
      'partner_request'            // New partnership requests
      // NOTE: make_offer and carrier_counter_offer are handled by dashboard "Action Required" cards
    ],
    payments: [
      'payment_received',         // Payment confirmations
      'invoice_generated',        // Invoice generated
      'invoice_received',         // New invoices from brokers
      'factoring_requested',      // Factoring requests
      'payment_status_updated'    // Payment status changes
    ]
  },
  BELL_ONLY: [
    'system_alert',               // System notifications
    'rating_received',            // Rating notifications
    'message_received',           // Message notifications
    'marketplace'                 // General marketplace info (when informational)
  ],
  // PRODUCTION FIX: Dashboard "Action Required" cards handle these types
  // They should NOT appear in sidebar dots or bell tray
  DASHBOARD_CARDS_ONLY: [
    'carrier_decline',            // Carrier rejected load - shows in "Action Required - Load Updates" section
    'make_offer',                 // Carrier offers - shows in "Action Required - Load Updates" section
    'carrier_counter_offer'       // Counter offers - shows in "Action Required - Load Updates" section
  ]
};

export const useEnhancedBrokerNotifications = () => {
  const [user, setUser] = useState<any>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationState, setNotificationState] = useState<any>(null); // Stores page/load visit timestamps
  const [counts, setCounts] = useState<NotificationCounts>({
    purchaseOrders: 0,
    shippingSchedule: 0,
    driverUpdates: 0,
    carrierPartners: 0,
    payments: 0,
    bellOnly: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(true);

  // Auth state listener
  useEffect(() => {
    const auth = getFailoverAwareAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
    return () => unsubscribeAuth();
  }, []);

  // Fetch initial notification state and subscribe to real-time notifications
  useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      setNotificationState(null);
      setCounts({ purchaseOrders: 0, shippingSchedule: 0, driverUpdates: 0, carrierPartners: 0, payments: 0, bellOnly: 0, total: 0 });
      return;
    }

    const setupNotifications = async () => {
      setLoading(true);
      const initialState = await getNotificationState(user.uid);
      setNotificationState(initialState);
      
      // Clean up old read notifications (run in background)
      cleanupReadNotifications(user.uid).catch(err => 
        Logger.warn('Failed to cleanup old notifications:', { err })
      );

      const db = getFailoverAwareDatabase();
      
      // Query for brokerId notifications (primary)
      // FIXED: Add limit and orderBy for scalability (Phase 4)
      const brokerQuery = query(
        collection(db, 'notifications'),
        where('brokerId', '==', user.uid),
        where('read', '==', false), // Only fetch unread notifications
        orderBy('createdAt', 'desc'),
        limit(100) // Limit to 100 most recent unread notifications
      );

      // Query for userId notifications (fallback)
      const userQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', user.uid),
        where('read', '==', false), // Only fetch unread notifications
        orderBy('createdAt', 'desc'),
        limit(100) // Limit to 100 most recent unread notifications
      );

      let brokerNotifications: Notification[] = [];
      let userNotifications: Notification[] = [];
      let brokerUnsubscribe: (() => void) | null = null;
      let userUnsubscribe: (() => void) | null = null;

      const processNotifications = () => {
        // Merge and deduplicate notifications
        const allNotifications = [...brokerNotifications, ...userNotifications];
        const uniqueNotifications = allNotifications.filter((notification, index, self) => 
          index === self.findIndex(n => n.id === notification.id)
        );
        
        setNotifications(uniqueNotifications);
        setLoading(false);
      };

      // Set up real-time listeners for both queries
      brokerUnsubscribe = onSnapshot(
        brokerQuery,
        (snapshot: QuerySnapshot<DocumentData>) => {
          brokerNotifications = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              type: data.type || 'unknown',
              read: data.read || false,
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
              ...data,
            };
          });
          processNotifications();
        },
        (error) => {
          Logger.error('Error fetching broker notifications:', error instanceof Error ? error : new Error(String(error)), { error });
          setLoading(false);
        }
      );

      userUnsubscribe = onSnapshot(
        userQuery,
        (snapshot: QuerySnapshot<DocumentData>) => {
          userNotifications = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              type: data.type || 'unknown',
              read: data.read || false,
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
              ...data,
            };
          });
          processNotifications();
        },
        (error) => {
          Logger.error('Error fetching user notifications:', error instanceof Error ? error : new Error(String(error)), { error });
          setLoading(false);
        }
      );

      return () => {
        if (brokerUnsubscribe) brokerUnsubscribe();
        if (userUnsubscribe) userUnsubscribe();
      };
    };

    setupNotifications();
  }, [user?.uid]);

  // Recalculate counts whenever notifications or notificationState changes
  useEffect(() => {
    // FIX: Only check if notificationState exists - pageVisits can be null/undefined (we have fallback)
    if (!notificationState || notifications.length === 0) {
      setCounts({ purchaseOrders: 0, shippingSchedule: 0, driverUpdates: 0, carrierPartners: 0, payments: 0, bellOnly: 0, total: 0 });
      return;
    }

    let purchaseOrdersCount = 0;
    let shippingScheduleCount = 0;
    let driverUpdatesCount = 0;
    let carrierPartnersCount = 0;
    let paymentsCount = 0;
    let bellOnlyCount = 0;
    let totalCount = 0;

    notifications.forEach((n) => {
      const category = getBrokerNotificationCategory(n.type);
      const notificationCreatedAt = n.createdAt instanceof Date ? n.createdAt : (n.createdAt?.toDate ? n.createdAt.toDate() : new Date());

      // Check if notification is newer than last visit for its category/page
      // Use the shared service function which includes 30-day fallback for null visits
      let isNew = true;
      // FIX: Safely access pageVisits with null checks
      const pageVisits = notificationState.pageVisits || {};
      if (category === 'purchaseOrders') {
        isNew = isNewerThanPageVisit({ ...n, createdAt: notificationCreatedAt }, pageVisits.purchaseOrders);
      } else if (category === 'shippingSchedule') {
        isNew = isNewerThanPageVisit({ ...n, createdAt: notificationCreatedAt }, pageVisits.shippingSchedule);
      } else if (category === 'driverUpdates') {
        isNew = isNewerThanPageVisit({ ...n, createdAt: notificationCreatedAt }, pageVisits.driverUpdates);
      } else if (category === 'carrierPartners') {
        isNew = isNewerThanPageVisit({ ...n, createdAt: notificationCreatedAt }, pageVisits.carrierPartners);
      } else if (category === 'payments') {
        isNew = isNewerThanPageVisit({ ...n, createdAt: notificationCreatedAt }, pageVisits.payments);
      }
      // For load-specific notifications, we'd need to check notificationState.loadVisits[n.loadId]
      // This will be handled by useLoadNotifications hook for specific load pages.

      if (isNew) {
        totalCount++;
        switch (category) {
          case 'purchaseOrders':
            purchaseOrdersCount++;
            break;
          case 'shippingSchedule':
            shippingScheduleCount++;
            break;
          case 'driverUpdates':
            driverUpdatesCount++;
            break;
          case 'carrierPartners':
            carrierPartnersCount++;
            break;
          case 'payments':
            paymentsCount++;
            break;
          case 'bellOnly':
            bellOnlyCount++;
            break;
          default:
            // If a notification type isn't explicitly categorized, it defaults to bellOnly
            bellOnlyCount++;
            break;
        }
      }
    });

    setCounts({
      purchaseOrders: purchaseOrdersCount,
      shippingSchedule: shippingScheduleCount,
      driverUpdates: driverUpdatesCount,
      carrierPartners: carrierPartnersCount,
      payments: paymentsCount,
      bellOnly: bellOnlyCount,
      total: totalCount,
    });
  }, [notifications, notificationState]);

  const markPageVisited = useCallback(async (page: 'purchaseOrders' | 'shippingSchedule' | 'driverUpdates' | 'carrierPartners' | 'payments' | 'dashboard') => {
    if (user?.uid) {
      const now = new Date();
      
      // Optimistically update local state first for immediate UI feedback
      // FIX: Ensure pageVisits exists before updating
      setNotificationState((prev: any) => {
        if (!prev) {
          // Initialize notificationState if it doesn't exist
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return {
            pageVisits: {
              purchaseOrders: thirtyDaysAgo,
              shippingSchedule: thirtyDaysAgo,
              driverUpdates: thirtyDaysAgo,
              carrierPartners: thirtyDaysAgo,
              payments: thirtyDaysAgo,
              dashboard: thirtyDaysAgo,
              [page]: now,
            },
            loadVisits: {},
            lastNotificationCheck: new Date(),
          };
        }
        return {
          ...prev,
          pageVisits: {
            ...(prev.pageVisits || {}),
            [page]: now,
          },
        };
      });
      
      // Update page visit timestamp in Firestore
      await updatePageVisit(user.uid, page);
      
      // Mark notifications as read for this page
      // This will cause them to disappear from the query (read == false)
      // which will trigger onSnapshot and recalculate counts automatically
      await markNotificationsAsRead(user.uid, page);
      
      // Note: Counts will automatically recalculate when notifications are marked as read
    }
  }, [user?.uid]);

  const markLoadVisited = useCallback(async (loadId: string) => {
    if (user?.uid) {
      // Update load visit timestamp
      await updateLoadVisit(user.uid, loadId);
      
      // Mark load-specific notifications as read
      await markLoadNotificationsAsRead(user.uid, loadId);
      
      // Optimistically update local state
      setNotificationState((prev: any) => ({
        ...prev,
        loadVisits: {
          ...prev.loadVisits,
          [loadId]: new Date(),
        },
      }));
    }
  }, [user?.uid]);

  const hasBellNotifications = counts.bellOnly > 0;

  return { counts, hasBellNotifications, markPageVisited, markLoadVisited, loading };
};

// Helper function to categorize broker notifications
const getBrokerNotificationCategory = (type: string): string | null => {
  // PRODUCTION FIX: Dashboard cards-only types should return null (not counted in sidebar dots or bell)
  if (BROKER_NOTIFICATION_ALLOCATION.DASHBOARD_CARDS_ONLY.includes(type)) {
    return null; // These are handled by dashboard "Action Required" cards, not sidebar/bell
  }
  
  for (const [category, types] of Object.entries(BROKER_NOTIFICATION_ALLOCATION.SIDEBAR_DOTS)) {
    if (types.includes(type)) {
      return category;
    }
  }
  if (BROKER_NOTIFICATION_ALLOCATION.BELL_ONLY.includes(type)) {
    return 'bellOnly';
  }
  return null;
};
